/**
 * Assets Module tab - manage dropdown options for Asset Type, Manufacturer, Status, Distributor
 * (Settings → Assets Module). Used by account Assets tab when adding/editing assets.
 */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import apiService from '../services/api';

const FIELD_CONFIG: { key: string; label: string }[] = [
  { key: 'asset_type', label: 'Asset Type' },
  { key: 'manufacturer', label: 'Manufacturer' },
  { key: 'status', label: 'Status' },
  { key: 'distributor', label: 'Distributor' },
];

interface OptionRow {
  id: string;
  field_name: string;
  value: string;
  label: string;
  sort_order: number;
}

interface AssetsModuleTabProps {
  onError?: (msg: string) => void;
}

const AssetsModuleTab: React.FC<AssetsModuleTabProps> = ({ onError }) => {
  const [fieldIndex, setFieldIndex] = useState(0);
  const [options, setOptions] = useState<OptionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingOption, setEditingOption] = useState<OptionRow | null>(null);
  const [form, setForm] = useState({ value: '', label: '', sort_order: 0 });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const currentField = FIELD_CONFIG[fieldIndex];

  const fetchOptions = async () => {
    if (!currentField) return;
    try {
      setLoading(true);
      setError(null);
      const list = await apiService.getAssetFieldOptionsByField(currentField.key);
      setOptions(list || []);
    } catch (e: any) {
      const msg = e?.response?.data?.detail || 'Failed to load options';
      setError(msg);
      if (onError) onError(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOptions();
  }, [currentField?.key]);

  const handleOpenAdd = () => {
    setEditingOption(null);
    const nextOrder = options.length ? Math.max(...options.map((o) => o.sort_order), 0) + 10 : 10;
    setForm({ value: '', label: '', sort_order: nextOrder });
    setDialogOpen(true);
  };

  const handleOpenEdit = (row: OptionRow) => {
    setEditingOption(row);
    setForm({ value: row.value, label: row.label, sort_order: row.sort_order });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    const trimmedValue = form.value.trim().toLowerCase().replace(/\s+/g, '_');
    const trimmedLabel = form.label.trim();
    if (!trimmedValue || !trimmedLabel) {
      setError('Value and label are required');
      return;
    }
    try {
      setSaving(true);
      setError(null);
      let newList: { value: string; label: string; sort_order: number }[];
      if (editingOption) {
        newList = options.map((o) =>
          o.id === editingOption.id
            ? { value: trimmedValue, label: trimmedLabel, sort_order: form.sort_order }
            : { value: o.value, label: o.label, sort_order: o.sort_order }
        );
      } else {
        if (options.some((o) => o.value === trimmedValue)) {
          setError('An option with this value already exists');
          setSaving(false);
          return;
        }
        newList = [
          ...options.map((o) => ({ value: o.value, label: o.label, sort_order: o.sort_order })),
          { value: trimmedValue, label: trimmedLabel, sort_order: form.sort_order },
        ];
      }
      await apiService.replaceAssetFieldOptions(currentField.key, newList);
      await fetchOptions();
      setDialogOpen(false);
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row: OptionRow) => {
    if (!window.confirm(`Remove option "${row.label}"?`)) return;
    try {
      setError(null);
      const newList = options
        .filter((o) => o.id !== row.id)
        .map((o) => ({ value: o.value, label: o.label, sort_order: o.sort_order }));
      await apiService.replaceAssetFieldOptions(currentField.key, newList);
      await fetchOptions();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to delete');
    }
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ flex: '1 1 auto', minWidth: 0 }}>
          Manage dropdown options for asset fields. These options are used in the account Assets tab when adding or
          editing assets.
        </Typography>
        <Tabs
          value={fieldIndex}
          onChange={(_, v) => setFieldIndex(v)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            minHeight: 36,
            flexShrink: 0,
            '& .MuiTabs-indicator': { height: 2, backgroundColor: 'primary.main' },
            '& .MuiTabs-flexContainer': { gap: 0 },
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '0.8125rem',
              minHeight: 36,
              py: 0,
              px: 1.5,
              minWidth: 'auto',
              '&.Mui-selected': { fontWeight: 600, color: 'primary.main' },
            },
          }}
        >
          {FIELD_CONFIG.map((f, i) => (
            <Tab key={f.key} label={f.label} value={i} />
          ))}
        </Tabs>
      </Box>
      <Paper>
        <Box display="flex" justifyContent="space-between" alignItems="center" px={2} py={1.5}>
          <Typography variant="subtitle1">{currentField?.label} options</Typography>
          <Button startIcon={<AddIcon />} variant="contained" size="small" onClick={handleOpenAdd}>
            Add option
          </Button>
        </Box>
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Value</TableCell>
                  <TableCell>Label</TableCell>
                  <TableCell align="right">Sort order</TableCell>
                  <TableCell align="right" width={100}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {options.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 3 }} color="text.secondary">
                      No options yet. Add one to get started.
                    </TableCell>
                  </TableRow>
                ) : (
                  options.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>{row.value}</TableCell>
                      <TableCell>{row.label}</TableCell>
                      <TableCell align="right">{row.sort_order}</TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => handleOpenEdit(row)} title="Edit">
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleDelete(row)} title="Delete" color="error">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingOption ? 'Edit option' : 'Add option'}</DialogTitle>
        <DialogContent>
          <TextField
            label="Value (internal)"
            value={form.value}
            onChange={(e) => setForm({ ...form, value: e.target.value })}
            fullWidth
            margin="normal"
            disabled={!!editingOption}
            helperText={
              editingOption
                ? 'Value cannot be changed when editing.'
                : 'Stored in database; use lowercase, underscores for spaces.'
            }
          />
          <TextField
            label="Label (display)"
            value={form.label}
            onChange={(e) => setForm({ ...form, label: e.target.value })}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Sort order"
            type="number"
            value={form.sort_order}
            onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value, 10) || 0 })}
            fullWidth
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AssetsModuleTab;
